import * as THREE from "three";

// audio variables:
const DISTANCE_THRESHOLD_SQUARED = 500;
const ROLL_OFF_NUMERATOR = 5;

export default class Client {
  constructor(id) {
    this.id = id;

    this.canvasElement = null;
    this.videoTexture = null;

    this.createAvatar();
  }

  attachToScene(scene) {
    if (!this.scene) {
      if (this.group) {
        console.log(`Adding client to scene: ${this.id}`);
        this.scene = scene;
        this.scene.add(this.group);
      } else {
        console.log(`No Client Avatar Found: ${this.id}`);
      }
    }
  }

  createAvatar() {
    const {
      group,
      canvasElement,
      videoTexture,
      videoMaterial,
    } = createAvatarGroup(this.id);

    this.group = group;
    this.canvasElement = canvasElement;
    this.videoTexture = videoTexture;
    this.videoMaterial = videoMaterial;
    this.desiredPosition = new THREE.Vector3();
    this.desiredRotation = new THREE.Quaternion();
    this.verticalOffset = 0; // for shrink zones
  }

  remove() {
    this.canvasElement.remove();
    this.scene.remove(this.group);
  }

  update() {
    this.interpolatePosition();
    this.interpolateRotation();
    this.updateVideoTexture();
  }

  setPosition(position) {
    this.desiredPosition = new THREE.Vector3(
      position[0],
      position[1] - this.verticalOffset,
      position[2]
    );
  }

  setScale(scale) {
    this.verticalOffset = 1 - scale;
    // set body to be narrow
    // this.group.children[0].scale.set(scale, 1, scale);
    // make head small
    // this.group.children[1].scale.set(scale, scale, scale);
    // this.group.children[2].scale.set(scale, scale, scale);
  }

  setRotation(rotation) {
    this.desiredRotation = new THREE.Vector3(
      rotation[0],
      rotation[1],
      rotation[2]
    );
  }

  interpolatePosition() {
    const snapDistance = 0.5;
    if (this.group) {
      this.group.position.lerp(this.desiredPosition, 0.2);
      if (this.group.position.distanceTo(this.desiredPosition) < snapDistance) {
        this.group.position.set(
          this.desiredPosition.x,
          this.desiredPosition.y,
          this.desiredPosition.z
        );
      }
    }
  }

  interpolateRotation() {
    let lookAtPoint = new THREE.Vector3();
    lookAtPoint.add(this.group.position);
    lookAtPoint.add(this.desiredRotation);
    lookAtPoint = new THREE.Vector3(
      lookAtPoint.x,
      this.group.position.y,
      lookAtPoint.z
    );
    this.group.lookAt(lookAtPoint);
  }

  updateVideoTexture() {
    // don't store this, as we may remove the video element elsewhere...
    // esentially, we are using the DOM to store state

    const remoteVideo = document.getElementById(`${this.id}_video`);

    if (remoteVideo != null && this.canvasElement != null) {
      redrawVideoCanvas(remoteVideo, this.canvasElement, this.videoTexture);
    }
  }

  updateAudio(silentZones) {
    // don't store this, as we may remove the audio element elsewhere...
    // esentially, we are using the DOM to store state
    const audioElement = document.getElementById(`${this.id}_audio`);
    if (audioElement) {
      const distSquared = this.getDistanceSquaredToCamera();
      if (distSquared > DISTANCE_THRESHOLD_SQUARED) {
        // TODO pause consumer here, rather than setting volume to zero
        audioElement.volume = 0;
      } else {
        // from lucasio here: https://discourse.threejs.org/t/positionalaudio-setmediastreamsource-with-webrtc-question-not-hearing-any-sound/14301/29
        const volumeFromDistance = Math.min(
          1,
          ROLL_OFF_NUMERATOR / distSquared
        );
        const maxVolume = silentZones.getMaxVolume(this.desiredPosition);
        audioElement.volume = Math.min(volumeFromDistance, maxVolume);
      }
    }
  }

  getDistanceSquaredToCamera() {
    const camera = this.scene.getObjectByName("mainCamera");
    const playerPosition = camera.position;
    const distSquared = playerPosition.distanceToSquared(this.group.position);
    return distSquared;
  }

  closeEnoughToConnect() {
    if (this.scene) {
      return this.getDistanceSquaredToCamera() <= DISTANCE_THRESHOLD_SQUARED;
    } else {
      return false;
    }
  }
}

function createAvatarGroup(clientID) {
  const sideMat = new THREE.MeshNormalMaterial();
  const frontMat = new THREE.MeshLambertMaterial({
    color: 0xffffff,
  });
  const bodyMaterials = [
    sideMat, // avatar's left
    sideMat, // avatar's right
    sideMat, // top
    sideMat, // bottom
    frontMat, // front
    sideMat, // back
  ];

  const _body = new THREE.Mesh(
    new THREE.BoxGeometry(0.5, 1, 0.5),
    bodyMaterials
  );

  _body.castShadow = true;

  const {
    canvasElement,
    videoTexture,
    videoMaterial,
  } = makeVideoTextureAndMaterial(clientID);

  const nonFacingMaterial = new THREE.MeshBasicMaterial({
    map: videoTexture,
    overdraw: true,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.35,
  });

  const materials = [
    nonFacingMaterial, // avatar's left
    nonFacingMaterial, // avatar's right
    nonFacingMaterial, // top
    nonFacingMaterial, // bottom
    videoMaterial, // front
    nonFacingMaterial, // back
  ];

  const _head = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), materials);

  _head.castShadow = true;

  const innerMaterial = new THREE.MeshLambertMaterial({ color: 0xffffff });

  const _innerHead = new THREE.Mesh(
    new THREE.BoxGeometry(0.99, 0.99, 0.99),
    innerMaterial
  );

  // set position of head before adding to parent object
  _body.position.set(0, 0, 0);
  _head.position.set(0, 1, 0);
  _innerHead.position.set(0, 1, 0);

  // https://threejs.org/docs/index.html#api/en/objects/Group
  const group = new THREE.Group();
  group.add(_body);
  group.add(_head);
  group.add(_innerHead);

  return { group, canvasElement, videoTexture, videoMaterial };
}

// Adapted from: https://github.com/zacharystenger/three-js-video-chat
function makeVideoTextureAndMaterial(_id) {
  // create a canvas and add it to the body
  const canvasElement = document.createElement("canvas");
  document.body.appendChild(canvasElement);

  canvasElement.id = `${_id}_canvas`;
  // canvasElement.width = videoWidth;
  // canvasElement.height = videoHeight;
  canvasElement.style = "visibility: hidden;";

  // get canvas drawing context
  const rvideoImageContext = canvasElement.getContext("2d");

  // background color if no video present
  rvideoImageContext.fillStyle = "#000000";
  rvideoImageContext.fillRect(0, 0, canvasElement.width, canvasElement.height);

  // make texture
  const videoTexture = new THREE.Texture(canvasElement);
  videoTexture.minFilter = THREE.LinearFilter;
  videoTexture.magFilter = THREE.LinearFilter;

  // make material from texture
  const videoMaterial = new THREE.MeshBasicMaterial({
    map: videoTexture,
    overdraw: true,
    side: THREE.DoubleSide,
  });

  return { canvasElement, videoTexture, videoMaterial };
}

// this function redraws on a 2D <canvas> from a <video> and indicates to three.js
// that the _videoTex should be updated
function redrawVideoCanvas(_videoEl, _canvasEl, _videoTex) {
  const _canvasDrawingContext = _canvasEl.getContext("2d");

  // check that we have enough data on the video element to redraw the canvas
  if (_videoEl.readyState === _videoEl.HAVE_ENOUGH_DATA) {
    // if so, redraw the canvas from the video element
    _canvasDrawingContext.drawImage(
      _videoEl,
      0,
      0,
      _canvasEl.width,
      _canvasEl.height
    );
    // and indicate to three.js that the texture needs to be redrawn from the canvas
    _videoTex.needsUpdate = true;
  }
}
